import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { Stream } from '@engoo/realtime-protocol/stream';
import { EntitySyncDocumentSpec } from 'app/models/entityStateSync/documentSpec';
import { KeyPathRootState, IKeyPathState } from 'app/models/entityStateSync/types';
import {
  EntityStateViewName,
  EntityStateKey,
  EntityStateValue,
  IEntityStateSetAction,
} from '@engoo/realtime-protocol/entityState';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { setEntityState } from 'app/models/entityStateSync/actions';
import { throwingFunction } from 'app/utils/throwingMethods';
import { getKeyPathState } from 'app/models/entityStateSync/selectors';
import { entityStateReducer } from 'app/models/entityStateSync/reducer';
import { Record } from 'immutable';
import { IMetadata } from 'app/services/rtc/sync/ActionStream';

const initialState = new KeyPathRootState();

interface ISetViewState<ViewName extends EntityStateViewName> {
  <Key extends EntityStateKey<ViewName>>(key: Key, value: EntityStateValue<ViewName, Key>): Promise<void>;
}

interface IAddStateSetAction {
  <
   ViewName extends EntityStateViewName,
   Key extends EntityStateKey<ViewName>,
   Value extends EntityStateValue<ViewName, Key>,
  >(keyPath: KeyPath, viewName: ViewName, key: Key, value: Value): Promise<void>;
}

interface IEntityStateSyncContext {
  entityKeyPathState: KeyPathRootState;
  addStateSetAction: IAddStateSetAction;
  metadata: IMetadata | undefined;
}

export const EntityStateSyncContext = React.createContext<IEntityStateSyncContext>({
  entityKeyPathState: initialState,
  addStateSetAction: throwingFunction,
  metadata: undefined,
});

export const EntityStateSyncDocumentProvider: React.FC = ({ children }) => {
  const {
    manager,
    isBlocked,
    metadata,
    documentState: entityKeyPathState,
  } = useDocumentManager(Stream.EntityState, EntitySyncDocumentSpec);

  // If not using shared state, actions will be applied to the local state rather than through a document manager
  const [localEntityState, setLocalEntityState] = useState(EntitySyncDocumentSpec.initialState);

  const entitiesState = !isBlocked && entityKeyPathState !== undefined ?
    entityKeyPathState :
    localEntityState;

  const addStateSetAction = useCallback<IAddStateSetAction>(async (keyPath, viewName, key, value) => {
    const action: IEntityStateSetAction = setEntityState(keyPath, viewName, key, value);
    if (manager !== undefined && !isBlocked) {
      await manager.addLocalAction(action);
    } else {
      const newState = entityStateReducer(localEntityState, action);
      setLocalEntityState(newState);
    }
  }, [manager, isBlocked, localEntityState, setLocalEntityState]);

  useEffect(() => {
    if (!isBlocked || !entityKeyPathState) {
      return;
    }

    // When switching from shared state to local state, update the local state
    // with the lastest version of the shared state
    setLocalEntityState(entityKeyPathState);
  }, [isBlocked, entityKeyPathState]);

  const value: IEntityStateSyncContext = useMemo(() => ({
    entityKeyPathState: entitiesState,
    addStateSetAction,
    metadata,
  }), [entitiesState, addStateSetAction, metadata]);

  return (
    <EntityStateSyncContext.Provider value={value}>
      {children}
    </EntityStateSyncContext.Provider>
  );
};

export const useEntityStateSyncContext = () => useContext(EntityStateSyncContext);

export const useEntitySyncState = <ViewName extends EntityStateViewName>(
  viewName: ViewName,
  keyPath: KeyPath,
): [Record<IKeyPathState<ViewName>> | undefined, ISetViewState<ViewName>] => {
  const { entityKeyPathState, addStateSetAction } = useEntityStateSyncContext();
  const keyPathState = getKeyPathState(entityKeyPathState, viewName, keyPath);

  const setViewState = useCallback<ISetViewState<ViewName>>(async (key, value) => {
    await addStateSetAction(keyPath, viewName, key, value);
  }, [keyPath, viewName, addStateSetAction]);

  return [keyPathState, setViewState];
};
